import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Flex,
  FormLabel,
  Icon,
  InputGroup,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { FormikProps } from 'formik';
import * as _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';

import {
  checkPurchasePercentageRateConfigErrors,
  prettyPrintAllFormulas,
  PurchasePercentageRateFormulaErrors,
} from '@finley-technologies/calc-agent';
import { FormulaEngine } from '@finley-technologies/finley-types';

import { WipConfig } from '../../types';
import { CreditAgreement } from '../common';
import { EditButton } from '../edit/EditButton';
import { EditPropertyModal } from '../edit/EditPropertyModal';
import { colors } from 'packages/common-frontend/colors';
import { InputFormField } from 'packages/common-frontend/components/FormField';
import { FormulaContainer } from 'packages/common-frontend/components/FormulaContainer';
import {
  Select,
  formatSelectOptions,
} from 'packages/common-frontend/components/Select';
import {
  errorToastAdminConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { FormulaEditor } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/MonacoEditor/ui/FormulaEditor';
import { FiCheck, FiAlertTriangle } from 'react-icons/fi';

const engineTypeOptions: {
  value: FormulaEngine.SupportedFormulaEngines;
  label: string;
}[] = formatSelectOptions(_.values(FormulaEngine.SupportedFormulaEngines));

const headers: (keyof CreditAgreement.DomainEntity.PurchasePercentageRateProperty)[] = [
  'name',
  'section',
  '_note_',
];

const propertyKey = 'purchasePercentageRate';

const getFormulaIcon = (errors: PurchasePercentageRateFormulaErrors | null) => {
  if (_.isNil(errors) || !errors.length) {
    return <Icon as={FiCheck} color="green.300" />;
  }
  return (
    <Tooltip
      label={
        <Stack>
          {errors.map((e: string) => (
            <Text key={e}>Missing Variable: {e}</Text>
          ))}
        </Stack>
      }
      placement="auto-start"
    >
      <Box>
        <Icon as={FiAlertTriangle} color="red.300" />
      </Box>
    </Tooltip>
  );
};

type LoanProductPurchasePercentageRateProps = {
  rate: CreditAgreement.DomainEntity.PurchasePercentageRateProperty;
  basePropertyKey: string;
  idx: number;
  baseFields: CreditAgreement.DataTapeField.BaseConfig[];
  intermediateCalcs: CreditAgreement.Config.LoanTapeField.IntermediateCalcs.IntermediateCalcContainer[];
  wipConfig?: WipConfig;
};
export const LoanProductPurchasePercentageRate = ({
  baseFields,
  basePropertyKey,
  idx,
  intermediateCalcs,
  rate,
  wipConfig,
}: LoanProductPurchasePercentageRateProps) => {
  const toast = useToast();
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const [error, setError] = useState<PurchasePercentageRateFormulaErrors>([]);

  const isClientWipMode = wipConfig?.operationMode === 'client';

  const fullPropertyKey = `${basePropertyKey}[${idx}].${propertyKey}`;

  useEffect(() => {
    const checkError = checkPurchasePercentageRateConfigErrors({
      purchasePercentageRateConfig: rate._config_.purchasePercentageRateConfig,
      baseFields,
      intermediateCalcs,
    });

    if (checkError && checkError.length && !toast.isActive(errorToastId)) {
      const description = (
        <Text>
          Purchase percentage rate is missing variables:
          {checkError.join(',')}
        </Text>
      );
      errorToastAdminConfig.title = `There Are Errors In The Config Formula`;
      toast({ ...errorToastAdminConfig, description });
    }
    if (!_.isEqual(checkError, error)) {
      setError(checkError);
    }
  }, [error, baseFields, intermediateCalcs, rate, toast]);

  const validate = useCallback(
    (
      values: CreditAgreement.DomainEntity.PurchasePercentageRateProperty,
    ): PurchasePercentageRateFormulaErrors => {
      // Config Errors
      const configErrors = checkPurchasePercentageRateConfigErrors({
        purchasePercentageRateConfig:
          values._config_.purchasePercentageRateConfig,
        baseFields,
        intermediateCalcs,
      });

      return configErrors;
    },
    [baseFields, intermediateCalcs],
  );

  const configPrefix = '_config_';
  const purchasePercentageRateConfigPrefix = 'purchasePercentageRateConfig';
  const fieldValuePrefix = `${configPrefix}.${purchasePercentageRateConfigPrefix}`;

  const ConditionalPropertiesComponent = useCallback(
    (localFormProps: FormikProps<any>, validationErrors: Array<string>) => {
      return (
        <>
          {!_.isEmpty(validationErrors) && (
            <Alert status="error" mb="20px">
              <AlertIcon />
              <AlertDescription>
                Errors:
                {validationErrors.join(',')}
              </AlertDescription>
            </Alert>
          )}

          {localFormProps.values[configPrefix][
            purchasePercentageRateConfigPrefix
          ].type === 'formula' && (
            <>
              {!isClientWipMode && (
                <Box mb="24px">
                  <FormLabel>Formula Engine</FormLabel>
                  <InputGroup width="100%">
                    <Select
                      value={
                        localFormProps.values[configPrefix][
                          purchasePercentageRateConfigPrefix
                        ].config.engineVersion
                      }
                      options={engineTypeOptions}
                      onChange={(engineVersion) => {
                        // @ts-ignore
                        const oldConfig: CreditAgreement.Config.PurchasePercentageRate.FormulaConfig =
                          localFormProps.values[configPrefix][
                            purchasePercentageRateConfigPrefix
                          ].config;
                        const newConfig = {
                          ...oldConfig,
                          engineVersion,
                        };
                        localFormProps.setFieldValue(
                          `${fieldValuePrefix}config`,
                          newConfig,
                        );
                      }}
                      isDisabled
                    />
                  </InputGroup>
                </Box>
              )}

              <FormLabel mb="8px">Formula</FormLabel>
              <Box
                border="1px"
                borderColor={colors.gray[200]}
                borderRadius="8px"
                p="8px"
              >
                <FormulaEditor
                  value={
                    localFormProps.values[configPrefix][
                      purchasePercentageRateConfigPrefix
                    ].config.formula
                  }
                  onChange={(value) => {
                    localFormProps.setFieldValue(
                      `${fieldValuePrefix}.config.formula`,
                      value,
                    );
                  }}
                  operationMode={wipConfig?.operationMode}
                  language="formula-purchasePercentageRate"
                />
              </Box>
            </>
          )}
        </>
      );
    },
    [fieldValuePrefix, isClientWipMode, wipConfig],
  );

  return (
    <>
      <Flex alignItems="center" mb="10px">
        <Text fontSize="20px" fontWeight="bold">
          Purchase Percentage Rate
        </Text>
      </Flex>
      <Box display="flex" mb="35px" width="100%" overflowY="auto">
        <Table borderWidth="1px">
          <Thead>
            <Tr>
              {headers.map((header) => (
                <Th
                  key={header}
                  minWidth={header === 'name' ? '15rem' : '10rem'}
                >
                  {_.startCase(header)}
                </Th>
              ))}
              {rate._config_.purchasePercentageRateConfig.type ===
                'formula' && (
                <>
                  <Th width="6rem" minWidth="6rem">
                    Valid
                  </Th>
                  <Th width="25rem" minWidth="25rem">
                    Formula
                  </Th>
                </>
              )}
              {wipConfig && <Th width="8.5rem" minWidth="8.5rem" />}
            </Tr>
          </Thead>
          <Tbody>
            <Tr>
              {headers.map((header) => {
                return (
                  <Td key={`${fullPropertyKey}.${String(header)}`}>
                    {/* !TODO @eldarshamukhamedov `rate[header]` lookup can return a
                      `{ purchasePercentageRateConfig: Config; }` object, which isn't a string. It's
                      not clear how it's meant to be rendered, but this is almost certainly a bug. */}
                    {/* @ts-expect-error @eldarshamukhamedov Implicit cast of object to string */}
                    {rate[header] ? rate[header] : '-'}
                  </Td>
                );
              })}
              {rate._config_.purchasePercentageRateConfig.type ===
                'formula' && (
                <>
                  <Td>{getFormulaIcon(error)}</Td>
                  <Td whiteSpace="pre-wrap">
                    <FormulaContainer>
                      {
                        rate._config_.purchasePercentageRateConfig.config
                          .formula
                      }
                    </FormulaContainer>
                  </Td>
                </>
              )}
              {wipConfig?.isMutateAllowed(fullPropertyKey) && (
                <Td>
                  <EditButton setIsOpen={setIsEditOpen} />
                  <EditPropertyModal<CreditAgreement.DomainEntity.PurchasePercentageRateProperty>
                    isOpen={isEditOpen}
                    setIsOpen={setIsEditOpen}
                    wipConfig={wipConfig}
                    basePropertyKey={`${fullPropertyKey}`}
                    conditionalProperties={
                      wipConfig?.isMutateAllowed(
                        `${basePropertyKey}[${idx}].purchasePercentageRate._config_.purchasePercentageRateConfig`,
                      )
                        ? {
                            conditionalPropertiesComponent: ConditionalPropertiesComponent,
                            conditionalPropertiesToggleLabel: 'Configuration',
                            conditionalPropertiesValidation: validate,
                            conditionalPropertiesOnSubmitTransform: (values) =>
                              prettyPrintAllFormulas(values),
                            useConditionalProperties: !!rate._config_
                              .purchasePercentageRateConfig,
                          }
                        : undefined
                    }
                  >
                    {(localFormProps) => (
                      <>
                        <InputFormField
                          propName="value"
                          label="Value"
                          formProps={localFormProps}
                          value={localFormProps.values.value}
                          placeholder="Enter value"
                        />
                      </>
                    )}
                  </EditPropertyModal>
                </Td>
              )}
            </Tr>
          </Tbody>
        </Table>
      </Box>
    </>
  );
};
